import type { CampaignTask } from '@bvengineering/cas-member-gateway-service-client';
import { Icon, Modal, Typography } from '@/primer/components';
import { getTaskDisplay } from '@/utils/taskTypes';

interface TaskConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  tasks: CampaignTask[];
  /** Connected social account handle to post from, if available. */
  handle?: string;
}

/** Confirmation modal listing the tasks a creator agrees to complete. */
export function TaskConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  tasks,
  handle,
}: TaskConfirmationModalProps) {
  const platforms = new Set(tasks.map((task) => getTaskDisplay(task.type).platform));
  const platform = platforms.size === 1 ? [...platforms][0] : 'social media';
  const showHandle = !!handle && platform === 'Instagram';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      primaryButton={{ label: 'Ok, got it!', onClick: onConfirm }}
    >
      <div className="flex flex-col gap-4">
        <Typography variant="header" size="h2" as="h2">
          Do you agree to complete the following task(s)?
        </Typography>

        <div className="flex flex-col gap-3">
          {tasks.map((task) => {
            const display = getTaskDisplay(task.type);
            return (
              <div key={task.id} className="flex items-center gap-3">
                <span className="flex items-center justify-center size-8 rounded-full bg-interactive-quiet flex-shrink-0">
                  <Icon name={display.icon} size="medium" color="default" />
                </span>
                <Typography variant="header" size="h3" as="h3">
                  {display.name}
                </Typography>
              </div>
            );
          })}
        </div>

        <Typography variant="body" size="medium" color="bodylight">
          You will need to post this content in your {platform} account.
        </Typography>

        {showHandle && (
          <div className="rounded-medium bg-surface-secondary px-4 py-3 text-center">
            <Typography variant="body" size="medium" as="span" decoration={{ bold: true }}>
              {handle}
            </Typography>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default TaskConfirmationModal;
